<template>
    <div
        class="font-industry relative flex h-full w-full flex-col p-4"
        :class="{
            'bg-black opacity-50': !isCurrentTurn,
        }"
    >
        <div class="flex items-center space-x-2">
            <img
                v-avatar-fallback="currentThrowingPlayerAvatar"
                :alt="currentThrowingPlayerName"
                class="h-auto w-12"
            />
            <div class="flex flex-col">
                <div class="font-bold">
                    <div
                        v-for="player in team.players"
                        :key="player.id"
                        class="line-clamp-1 max-w-20 truncate"
                        :class="getPlayerClasses(player)"
                    >
                        {{ player.tag_name }}
                    </div>
                </div>
                <div v-if="average" class="text-xs font-semibold text-green-500">
                    {{ averageLabel }}: {{ average }}
                </div>
            </div>
        </div>

        <div class="mt-4 flex items-center justify-center gap-2 text-sm">
            <span class="text-titles font-semibold tracking-wider uppercase">{{ $t('play_team.sets') }}</span>
            <span class="font-bold text-white">{{ playerSetsWon }}</span>
            <span class="text-titles">|</span>
            <span class="text-titles font-semibold tracking-wider uppercase">{{ $t('play_team.legs') }}</span>
            <span class="font-bold text-white">{{ playerLegsWon }}</span>
        </div>

        <div
            v-if="isX01Game && remainingScore !== null && remainingScore !== undefined"
            class="mt-3 flex flex-1 flex-col text-center"
        >
            <div class="text-5xl font-bold text-white">
                {{ remainingScore }}
            </div>

            <div class="relative mt-4 flex-1">
                <div
                    v-if="
                        !matchStore.isMatchOver &&
                        !matchStore.isRealtimePaused &&
                        matchStore.botThrowIndicator.visible &&
                        String(matchStore.botThrowIndicator.teamId) === String(team.id)
                    "
                    class="pointer-events-none absolute top-0 left-1/2 z-40 -translate-x-1/2"
                >
                    <div
                        class="border-stroke-1 w-44 rounded-md border bg-black/75 px-3 py-2 shadow-2xl backdrop-blur-sm"
                    >
                        <div class="flex items-center gap-2">
                            <svg
                                class="h-4 w-4 animate-spin text-red-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    class="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    stroke-width="4"
                                />
                                <path
                                    class="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                            <span class="text-xs font-bold tracking-wider text-white uppercase">
                                {{ $t('play_team.dartbot_throwing') }}
                            </span>
                        </div>
                        <div class="mt-1 text-xs text-white/80">
                            {{ $t('play_team.throw_step', { n: matchStore.botThrowIndicator.step }) }}
                        </div>
                        <div class="mt-2 flex items-center gap-1">
                            <span
                                v-for="step in 3"
                                :key="step"
                                class="h-1.5 flex-1 rounded-full transition-colors"
                                :class="
                                    step <= matchStore.botThrowIndicator.step
                                        ? 'bg-red-500'
                                        : 'bg-white/20'
                                "
                            ></span>
                        </div>
                    </div>
                </div>
                <div
                    ref="scrollContainer"
                    class="custom-scrollbar absolute h-full min-h-30 w-full overflow-y-auto pr-2"
                >
                    <div
                        v-for="(turn, turnIndex) in lastThrows"
                        :key="turnIndex"
                        class="-mx-2 mr-3 flex items-center gap-2 rounded px-2 py-2 transition-all"
                        :class="{
                            'justify-start': this.index === 0,
                            'flex-row-reverse justify-start': this.index === 1,
                            'cursor-pointer': !isTurnFromBot(turn),
                            'cursor-default': isTurnFromBot(turn),
                        }"
                        @click="handleTurnClick(turn, turnIndex)"
                    >
                        <div
                            class="font-industry font-bold text-[#797979]"
                            :class="{
                                'text-xs': turnIndex !== lastThrows.length - 1,
                                'text-sm': turnIndex === lastThrows.length - 1,
                            }"
                        >
                            {{ turn.remaining_score + turn.score }}
                        </div>
                        <div
                            class="bg-card border-stroke-1 font-industry flex items-center justify-center gap-1 border-1 text-white"
                            :class="{
                                'h-6 w-12 text-xs font-normal': turnIndex !== lastThrows.length - 1,
                                'h-8 w-[74px] text-sm font-bold':
                                    turnIndex === lastThrows.length - 1,
                                'flex-row-reverse': this.index === 1,
                                'border !border-red-500 bg-red-900/40': matchStore.isEditingTurn(
                                    team.id,
                                    turnIndex,
                                ),
                            }"
                        >
                            <span
                                :class="{
                                    'text-[#FFC227]': turn.score === 180,
                                }"
                            >
                                {{ turn.score }}
                            </span>
                            <svg
                                v-if="turnIndex === lastThrows.length - 1 && !isTurnFromBot(turn)"
                                class="h-3 w-3"
                                :class="{
                                    'text-[#FFC227]': turn.score === 180,
                                    'text-white': turn.score !== 180,
                                }"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                stroke-width="2"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                />
                            </svg>
                        </div>
                    </div>
                </div>
                <div
                    class="pointer-events-none absolute right-0 bottom-[-15px] left-0 h-6"
                    :class="{
                        'bg-gradient-to-t from-black to-transparent': !isCurrentTurn,
                        'from-bg bg-gradient-to-t to-transparent': isCurrentTurn,
                    }"
                ></div>
            </div>
        </div>

        <div v-else-if="isCricketGame" class="mt-3 text-center">
            <div class="text-5xl font-bold text-white">
                {{ cricketScore }}
            </div>
        </div>

        <div
            v-if="isX01Game"
            class="mt-4"
            :class="{
                'opacity-0': !isCurrentTurn || !checkoutOptions,
            }"
        >
            <div class="mb-2">{{ $t('play_team.to_finish') }}</div>
            <div class="flex min-h-8 gap-2">
                <div
                    v-for="(dart, index) in getCheckoutDarts()"
                    :key="index"
                    class="bg-card border-stroke-1 flex h-8 w-10 items-center justify-center border-1 text-xs font-bold text-red-600"
                >
                    {{ dart }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useMatchStore } from '@/stores/match.js'
import checkoutService from '@/services/checkout.js'
import { MATCH_TYPES } from '@/constants/matchTypes.js'

export default {
    name: 'PlayTeam',
    props: {
        index: {
            type: Number,
            required: true,
        },
        team: {
            type: Object,
            default: null,
        },
        isBotTurn: {
            type: Boolean,
            default: false,
        },
        // Overrides whose turn is shown (e.g. the owner of a turn being edited)
        activeProfileId: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {}
    },
    computed: {
        matchStore() {
            return useMatchStore()
        },
        currentMatchTypeId() {
            return this.matchStore.getMatchTypeId
        },
        isX01Game() {
            return this.currentMatchTypeId === MATCH_TYPES.X01
        },
        isCricketGame() {
            return this.currentMatchTypeId === MATCH_TYPES.CRICKET
        },
        teamName() {
            return this.team?.name || 'Team'
        },
        remainingScore() {
            if (!this.isX01Game) return null
            return this.matchStore.getCompetitorRemainingScore(this.team.id)
        },
        cricketScore() {
            if (!this.isCricketGame || !this.team?.id) return 0

            const sets = this.matchData?.sets || []
            if (!sets.length) return 0

            const currentSet = sets[sets.length - 1]
            if (!currentSet?.legs?.length) return 0

            const currentLeg = currentSet.legs[currentSet.legs.length - 1]
            if (!currentLeg?.turns?.length) return 0

            // For teams, get the most recent turn from any team member
            const teamTurns = currentLeg.turns.filter((turn) => turn.team === this.team.id)
            if (!teamTurns.length) return 0

            const latestTurn = teamTurns[teamTurns.length - 1]
            return latestTurn?.state?.score || 0
        },
        activePlayerId() {
            return this.activeProfileId ?? this.matchStore.getCurrentPlayer?.id ?? null
        },

        isCurrentTurn() {
            if (this.activePlayerId == null || !this.team?.players) return false
            return this.team.players.some((player) => player.id === this.activePlayerId)
        },

        currentThrowingPlayerData() {
            if (this.activePlayerId == null || !this.team?.players) return null
            return this.team.players.find((player) => player.id === this.activePlayerId) || null
        },
        currentThrowingPlayerAvatar() {
            return this.currentThrowingPlayerData?.avatar
        },
        currentThrowingPlayerName() {
            return this.currentThrowingPlayerData?.tag_name
        },
        lastThrows() {
            return this.matchStore.getCompetitorLastThrows(this.team.id)
        },
        average() {
            return this.matchStore.getCompetitorAverage(this.team.id)
        },
        averageLabel() {
            return this.isCricketGame
                ? this.$t('play_team.three_turn_marks')
                : this.$t('play_team.three_turn_avg')
        },
        checkoutOptions() {
            if (!this.isX01Game || !this.remainingScore || isNaN(this.remainingScore)) return null
            return checkoutService.getCheckoutMessage(this.remainingScore)
        },
        matchData() {
            return this.matchStore.getMatchData
        },
        playerSetsWon() {
            return this.matchStore.getCompetitorSetsWon(this.team.id)
        },
        playerLegsWon() {
            return this.matchStore.getCompetitorLegsWon(this.team.id)
        },
    },
    methods: {
        isCurrentThrowingPlayer(player) {
            return this.activePlayerId != null && player.id === this.activePlayerId
        },

        getPlayerClasses(player) {
            return {
                'text-white': this.isCurrentThrowingPlayer(player),
                'text-zinc-600': !this.isCurrentThrowingPlayer(player),
            }
        },

        scrollToBottom() {
            if (this.$refs.scrollContainer) {
                this.$refs.scrollContainer.scrollTop = this.$refs.scrollContainer.scrollHeight
            }
        },
        getCheckoutDarts() {
            if (!this.checkoutOptions) return []

            const checkoutMessage = this.checkoutOptions

            if (checkoutMessage.includes(' + ')) {
                return checkoutMessage.split(' + ').map((part) => part.split(' = ')[0])
            }

            if (checkoutMessage.includes(' = ')) {
                return [checkoutMessage.split(' = ')[0]]
            }

            return [checkoutMessage]
        },
        handleTurnClick(turn, turnIndex) {
            if (this.isTurnFromBot(turn)) return
            this.matchStore.setEditingTurn(this.team.id, turnIndex, turn)
        },
        isTurnFromBot(turn) {
            if (!turn?.fullTurn) return false
            return this.matchStore.isTurnFromBot(turn.fullTurn)
        },
    },
    watch: {},
    mounted() {
        if (this.isX01Game) {
            this.scrollToBottom()
        }
    },
    updated() {
        if (this.isX01Game) {
            this.scrollToBottom()
        }
    },
    beforeUnmount() {},
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
    width: 2px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: #2b2b2b;
    border-radius: 0;
}

.custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #2b2b2b transparent;
}
</style>
